import { Injectable } from '@angular/core';
import { Encoding } from '@mode-vegas/vegas';
import { ChartFacade } from '@mode/charts/contract';
import { DataViewTypes, RUNNING_QUERY_STATES, ReportTypes } from '@mode/shared/contract-common';
import { getFormattedDuration, isPresent } from '@mode/shared/util-js';
import { DataViewFacade } from '@mode/switch-views/contract';
import { Store } from '@ngrx/store';
import { ChartFormat, FieldFormatMap } from 'libs/shared/contract-common/src/lib/charts';
import { Observable } from 'rxjs';
import { filter, map, switchMap } from 'rxjs/operators';
import * as ReportEditorActions from './report-editor/report-editor.actions';
import * as ReportEditorSelectors from './report-editor/report-editor.selectors';
import { ReportEditorDomainState } from './report-editor/report-editor.selectors';
/* eslint-disable @typescript-eslint/member-ordering */
@Injectable()
export class ReportEditorFacade {
  constructor(
    private store: Store<ReportEditorDomainState>,
    private chartFacade: ChartFacade,
    dataViewFacade: DataViewFacade
  ) {
    this.selectedQueryToken$ = store.select(ReportEditorSelectors.selectSelectedQueryToken);
    this.editorLayout$ = store.select(ReportEditorSelectors.selectEditorLayout);
    this.importDatasetResponse$ = store.select(ReportEditorSelectors.selectImportDatasetResponse);

    this.selectedQueryRunState$ = this.selectedQueryToken$.pipe(
      filter(isPresent),
      switchMap((queryToken) => dataViewFacade.getDatasetResultByQuery(queryToken)),
      map((result) => {
        if (result != null) {
          const rowsCountVisible =
            result.queryRunState === DataViewTypes.QueryRunStatus.Succeeded &&
            result.rowCount != null &&
            result.rowCount > 0;
          // This is the same as the above, replicating these two values from the legacy context they end
          // up representing the same value.
          const isVisible = rowsCountVisible;
          const end = new Date(result.completedAt);
          const start = new Date(result.createdAt);
          const elapsed = Math.max(0, end.getTime() - start.getTime());

          return {
            isVisible,
            dataset: {
              content: result.legacyContent,
              columns: result.metaColumns,
              oversized: result.oversized,
              json: result.jsonUrl,
              csv: result.csvUrl,
            },
            datasetSizeBytes: result.bytes,
            executing: RUNNING_QUERY_STATES.includes(result.queryRunState),
            rowsCountVisible,
            executionTime: getFormattedDuration(elapsed),
            rowsCount: result.rowCount,
            rowsCountMessage: rowsCountVisible ? `${result.rowCount} rows returned` : ``,
          };
        } else {
          return {
            isVisible: false,
            dataset: undefined,
            executing: false,
            datasetSizeBytes: undefined,
            rowsCountMessage: '',
          };
        }
      })
    );
  }

  selectedQueryToken$: Observable<string | undefined>;
  editorLayout$: Observable<string | undefined>;
  importDatasetResponse$: Observable<any | undefined>;

  getChartsMetadataObservable(switchViewToken: string) {
    return this.chartFacade.createChartsMetadataBySwitchViewToken(switchViewToken);
  }

  importDatasetWithChartFromSearch(
    accountId: string,
    reportId: string,
    queryToken: string,
    chartColorPaletteToken: string,
    encoding: Encoding,
    format: ChartFormat,
    fieldFormats: FieldFormatMap
  ) {
    this.store.dispatch(
      ReportEditorActions.importDatasetWithChartFromSearch({
        accountId,
        reportId,
        queryToken,
        chartColorPaletteToken,
        encoding,
        format,
        fieldFormats,
      })
    );
  }

  importDatasetWithChartFromUrl(
    accountId: string,
    reportId: string,
    queryToken: string,
    chartColorPaletteToken: string,
    encoding: Encoding,
    format: ChartFormat,
    fieldFormats: FieldFormatMap
  ) {
    this.store.dispatch(
      ReportEditorActions.importDatasetWithChartFromUrl({
        accountId,
        reportId,
        queryToken,
        chartColorPaletteToken,
        encoding,
        format,
        fieldFormats,
      })
    );
  }

  importDatasetSuccess(accountId: string, reportId: string, switchViewToken: string, vizToken: string | undefined) {
    this.store.dispatch(ReportEditorActions.importDatasetSuccess({ accountId, reportId, switchViewToken, vizToken }));
  }

  clearImportDatasetResponse() {
    this.store.dispatch(ReportEditorActions.clearImportDatasetResponse());
  }

  /**
   * Legacy observable for compatibility. Do not use this for new components, instead
   * write new granular observables.
   */
  selectedQueryRunState$: Observable<ReportTypes.SelectedQueryRunState>;

  selectLegacyTable(token: string, queryToken: string) {
    this.store.dispatch(ReportEditorActions.selectLegacyTable({ token, queryToken }));
  }

  selectQuery(token: string) {
    this.store.dispatch(ReportEditorActions.selectQuery({ token }));
  }

  updateReportLayout(layout: string) {
    this.store.dispatch(ReportEditorActions.updateReportLayout({ layout }));
  }

  // M2 Datasets contracts, will need to move to more specific facades

  /**
   *
   * @param reportToken
   * @param chartToken
   * @param context the origin of this action
   */
  addChartToBuilder(reportToken: string, chartToken: string) {}

  pollAllRuns(reportToken: string) {
    // Defer to query-run facade to load the reports query runs
  }

  runQuery(queryToken: string) {}

  runAllQueries(reportToken: string) {}

  createQuery(newQuery: any /* Type TBD */) {}

  deleteQuery(queryToken: string) {}

  renameQuery(renameQuery: any /* Type TBD */) {}

  duplicateQuery(queryToken: string) {}
}
